import React from 'react';
import SeeDefinitions from '../../styled/SeeDefinitions';
import HoldingsReallocatedStatement from '../../styled/HoldingsReallocatedStatement';
import DonutChartTable from '../../charts/DonutChartTable';
import TRP from '../../shared/TRP';
import { kebabCase } from 'lodash';

import { DONUT_CHART_DEFAULT_OPTIONS } from '../../../utils/constants';
import { standardDateFormat } from '../../../utils/dateFormatter';
import { formatPercentage, formatNumber } from '../../../utils/numberFormatter';

import styles from './FixedIncomeChartTable.module.scss';

type Props = {
  className?: string;
  selectedAccount: AssetAccount;
};

const FixedIncomeChartTable: React.FC<Props> = ({
  className,
  selectedAccount
}) => {
  if (selectedAccount === undefined) {
    return null;
  }

  const {
    asOfDate,
    profile: { fixedIncome }
  } = selectedAccount.monthlyReport;

  if (fixedIncome === undefined) {
    return null;
  }

  const fixedIncomeValues = fixedIncome || [];
  const {
    weightedAverageMaturity,
    yieldAtMarket,
    maturityDistributions,
    weightedAverageEffectiveDuration,
    weightedAverageCreditQuality,
    creditRatingDistributions
  } = fixedIncomeValues;

  const maturityDistributionsData = maturityDistributions || [];
  const maturityData: DataPoints = maturityDistributionsData.map((dist) => ({
    label: dist.yearRange,
    value: dist.maturityDistribution,
    style: styles[`fi-${kebabCase(dist.yearRange)}`]
  }));

  const maturityChartOptions = {
    ...DONUT_CHART_DEFAULT_OPTIONS,
    classNames: [
      styles['fi-0-1-years-chart'],
      styles['fi-1-3-years-chart'],
      styles['fi-3-5-years-chart'],
      styles['fi-5-7-years-chart'],
      styles['fi-7-10-years-chart'],
      styles['fi-10-20-years-chart'],
      styles['fi-20-30-years-chart'],
      styles['fi-30-years-chart']
    ]
  };

  const creditRatingDistributionsData = creditRatingDistributions || [];

  const creditData: DataPoints = creditRatingDistributionsData.map((dist) => ({
    label: dist.ratingLevel,
    value: dist.creditRatingDistribution,
    style: styles[`fi-${kebabCase(dist.ratingLevel)}`]
  }));

  const creditChartOptions = {
    ...DONUT_CHART_DEFAULT_OPTIONS,
    classNames: creditRatingDistributionsData.map(
      (dist) => styles[`fi-${kebabCase(dist.ratingLevel)}-chart`]
    )
  };

  return (
    <div className={`${className} ${styles['fixed-income-flex']}`}>
      <div className={styles['holdings-title']}>Portfolio Profile</div>
      <div className={styles['title-section']}>
        <span className={styles['table-type']}>Fixed Income</span>
        <span className={styles['table-date']}>
          As of {standardDateFormat(asOfDate)}
        </span>
      </div>
      <div className={styles['charts']}>
        <div className={`${styles['charts']}`}>
          <DonutChartTable
            className={styles['donut-chart-table']}
            tableType="Maturity Distribution"
            tableTitle="As a percent of Total Fixed Income"
            tableTitleClassName={styles['table-title']}
            chartOptions={maturityChartOptions}
            dataPoints={maturityData}
            donutChartClassName={styles['donut-chart']}
            contentClassName={styles['chart-content']}
            tableTypeClassName={styles['chart-title']}
          />
        </div>
        <div className={`${styles['charts']} ${styles['last']}`}>
          <DonutChartTable
            className={styles['donut-chart-table']}
            tableType="S&P Credit Ratings"
            tableTitle="As a percent of Total Fixed Income"
            tableTitleClassName={styles['table-title']}
            chartOptions={creditChartOptions}
            dataPoints={creditData}
            donutChartClassName={styles['donut-chart']}
            contentClassName={styles['chart-content']}
            tableTypeClassName={styles['chart-title']}
          />
        </div>
      </div>
      <div className={styles['disclaimer']}>
        <div className={styles['wt-avg-values']}>
          <div className={`${styles['chart']}`}>
            <div className={styles['other-values']}>
              <div
                className={`${styles['other-value']} ${styles['weighted-average']}`}>
                <div className={styles['label']}>Weighted Avg. Maturity</div>
                <div className={styles['weighted-avg-value']}>
                  {formatNumber(weightedAverageMaturity)} years
                </div>
              </div>
              <div
                className={`${styles['other-value']} ${styles['yield-at-market']}`}>
                <div className={styles['label']}>
                  Weighted Avg. Eff. Duration
                </div>
                <div className={styles['weighted-avg-value']}>
                  {formatNumber(weightedAverageEffectiveDuration)} years
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles['chart']} ${styles['last']}`}>
            <div className={styles['other-values']}>
              <div
                className={`${styles['other-value']} ${styles['weighted-avg-value']}`}>
                <div className={styles['label']}>
                  Weighted Avg. Credit Quality
                </div>
                <div className={styles['weighted-avg-value']}>
                  {weightedAverageCreditQuality}
                </div>
              </div>
              <div
                className={`${styles['other-value']} ${styles['yield-at-market']}`}>
                <div className={styles['label']}>Estimated Yield at Market</div>
                <div className={styles['weighted-avg-value']}>
                  {formatPercentage(yieldAtMarket)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <SeeDefinitions />
        <HoldingsReallocatedStatement />
        <div className={styles['legal-disclaimer']}>
          Sources for Credit Quality: Standard &amp; Poor’s (S&amp;P) <TRP />{' '}
          does not evaluate these ratings but simply assigns them to the
          appropriate credit quality category as determined by the rating
          agency.
        </div>
      </div>
    </div>
  );
};

export default FixedIncomeChartTable;
